import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { logisticCartApi } from 'redux/services/logistic-cart'
import { CreateLogisticCart } from 'redux/services/logistic-cart/types'

import { AppButton } from 'components/AppButton'
import AppDatePicker from 'components/AppDatePicker/AppDatePicker'
import { AppModal } from 'components/AppModal'
import { AppSelect } from 'components/AppSelect'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH, TextFieldPhone } from 'components/TextFieldPhone'

import { CART_TYPES, URGENCY_TYPES } from 'constants/common'
import { CartType } from 'models/common'
import {
  LogisticCart,
  LogisticCartStatus,
  LogisticCartUrgency,
} from 'models/logistic_cart'

import styles from './Edit.module.scss'

interface Props {
  data: LogisticCart
}
export function EditLogisticModal({ data }: Props) {
  const [open, setOpen] = useState(false)
  const {
    watch,
    control,
    register,
    clearErrors,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<CreateLogisticCart>({
    defaultValues: {
      client_full_name: data.client_full_name,
      cart_type: data.cart_type,
      pan: data.pan,
      degree_urgency: data.degree_urgency,
      comment: data.comment,
      phone: data.phone,
      date_embossment: data.date_embossment?.split('/').reverse().join('-'),
      status: LogisticCartStatus.waiting,
    },
  })
  const [submit, submitState] = logisticCartApi.useEditLogisticCartMutation()

  function onSubmit(values: CreateLogisticCart) {
    submit({ id: data.id, body: values })
      .unwrap()
      .then(() => {
        closeModal()
      })
      .catch(err => {
        try {
          Object.entries(err.data.errors).forEach(([key, messages]) =>
            setError(key as keyof CreateLogisticCart, {
              type: 'custom',
              message: Array.isArray(messages) ? messages[0] : messages,
            }),
          )
        } catch (error) {
          console.error(error)
        }
      })
  }
  const openModal = () => {
    setOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setOpen(false)
    document.body.style.overflow = 'auto'
  }

  return (
    <>
      <AppButton onClick={openModal}>Редактировать</AppButton>
      {open && (
        <AppModal onClose={closeModal} title={`Редактирование заявки`}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
            <TextField
              {...register('client_full_name', {
                required: 'Обязательное поле',
              })}
              required
              label="ФИО клиента"
              withError
              error={errors.client_full_name}
            />
            <AppDatePicker
              required
              type="date"
              onchange={e => {
                setValue('date_embossment', e)
                clearErrors('date_embossment')
              }}
              value={watch('date_embossment')}
              label="Дата эмбоссирования"
              withError
              error={errors.date_embossment}
            />
            <TextField
              {...register('comment')}
              label="Комментарий"
              withError
              error={errors.comment}
            />
            <AppSelect
              label="Тип"
              text={watch('cart_type') ? CART_TYPES[watch('cart_type')] : ''}
              onChangeValues={values => {
                if (values[0]) {
                  setValue('cart_type', values[0] as CartType)
                  clearErrors('cart_type')
                }
              }}
              required
              list={Object.entries(CART_TYPES).map(([value, title]) => ({
                title,
                value,
              }))}
              withError
              error={errors.cart_type}
            />
            <Controller
              control={control}
              name="phone"
              rules={{
                required: 'Введите телефон',
                min: {
                  value: KG_PHONE_MAX_LENGTH,
                  message: 'Введите телефон полностью',
                },
              }}
              render={({ field: { value } }) => {
                return (
                  <TextFieldPhone
                    inputSize="small"
                    onChangePhone={phone => {
                      setValue('phone', phone.formattedPhone)
                    }}
                    value={value}
                    withError
                    error={errors.phone}
                  />
                )
              }}
            />
            <AppSelect
              label="Степень срочности"
              text={URGENCY_TYPES[watch('degree_urgency')!] || ''}
              onChangeValues={values => {
                if (values[0]) {
                  setValue('degree_urgency', values[0] as LogisticCartUrgency)
                  clearErrors('degree_urgency')
                }
              }}
              required
              list={Object.entries(URGENCY_TYPES).map(([value, title]) => ({
                title,
                value,
              }))}
              withError
              error={errors.cart_type}
            />
            <TextField
              required
              value={watch('pan')}
              onChange={e => {
                setValue(
                  'pan',
                  e.currentTarget.value.replace(/\D/g, '').slice(0, 4),
                )
                clearErrors('pan')
              }}
              label="ПАН Карты"
              withError
              error={errors.pan}
            />

            <AppButton disabled={submitState.isLoading} type="submit">
              Сохранить
            </AppButton>
          </form>
        </AppModal>
      )}
    </>
  )
}
